import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { encode, decode } from '@msgpack/msgpack';
import { environment } from '../../environments/environment';
import { DebugLogService } from '../shared/debug-log.service';
import { AuthService } from '../services/auth.service';
import { T2dToastService } from '@tec2date/ui-components';
import { MaintenanceStatusService } from '../services/maintenance-status.service';
import { parseProblemDetails } from '../utils/problem-details.util';

/** HTTP-Methoden die einen Request-Body mit MessagePack serialisieren. */
const BODY_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

function safePath(url: string | null): string {
  if (!url) return '';
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

/**
 * Versucht, einen Response-Body-ArrayBuffer in ein Objekt zu dekodieren.
 *
 * Hintergrund: Erfolgreiche Antworten kommen aus den Symfony-Controllern als
 * `application/x-msgpack`, aber **Fehler-Antworten** werden vom
 * `GlobalExceptionHandler` als `application/json` zurückgegeben (siehe
 * `backend/src/Inthepro/ErrorHandler/GlobalExceptionHandler.php`). Wir
 * sehen daher beide Encodings im selben `responseType: 'arraybuffer'`-
 * Kanal und müssen anhand des `Content-Type`-Headers (mit Fallback auf
 * Trial-and-Error) entscheiden, wie dekodiert wird.
 */
function decodeBodyBuffer(buf: ArrayBuffer, contentType: string | null): unknown | null {
  if (buf.byteLength === 0) return null;
  const ct = (contentType ?? '').toLowerCase();
  const bytes = new Uint8Array(buf);

  // 1. application/json (z.B. Error-Antworten aus dem GlobalExceptionHandler)
  if (ct.includes('application/json') || ct.includes('+json')) {
    try {
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch {
      /* fallthrough */
    }
  }
  // 2. application/x-msgpack (Default für Erfolgsantworten)
  if (ct.includes('msgpack') || ct === '') {
    try {
      return decode(bytes);
    } catch {
      /* fallthrough */
    }
  }
  // 3. Unbekannter Content-Type oder Decoder gescheitert: beide Varianten
  //    nochmal probieren, der robusteste Pfad gewinnt.
  try {
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    /* try msgpack */
  }
  try {
    return decode(bytes);
  } catch {
    /* give up */
  }
  return null;
}

/**
 * Entfernt rekursiv alle Keys mit `undefined`-Werten aus einem Objekt.
 *
 * Hintergrund: `JSON.stringify` lässt `undefined`-Werte automatisch weg,
 * `@msgpack/msgpack.encode` hingegen encoded sie als `nil`. Für Symfony-DTOs
 * mit optionalen Constructor-Args (`?string $x = null`) ist das in der Regel
 * unkritisch, aber für required-Args (`string $x`) führt ein übertragenes nil
 * zu einer `MissingConstructorArgumentsException` statt dass das Default
 * greift. `null` wird bewusst NICHT gefiltert — nur `undefined` (explizit
 * "nicht gesetzt" im Frontend-Sinne).
 */
function stripUndefined(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => stripUndefined(v));
  }
  if (value instanceof Date || value instanceof ArrayBuffer || ArrayBuffer.isView(value)) {
    return value;
  }
  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      if (val === undefined) continue;
      result[key] = stripUndefined(val);
    }
    return result;
  }
  return value;
}

/**
 * Zentraler API-Interceptor:
 * - Prefixed relative URLs mit environment.apiUrl
 * - Setzt withCredentials für JWT-Cookie-Authentifizierung
 * - Serialisiert den Request-Body als MessagePack (POST/PUT/PATCH/DELETE)
 * - Setzt Content-Type und Accept Header für MessagePack
 * - Deserialisiert den Response-Body aus MessagePack
 * - Leitet bei 401-Antworten zu /login um
 */
export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const debugLog = inject(DebugLogService);
  const authService = inject(AuthService);
  const toastService = inject(T2dToastService);
  const maintenanceStatusService = inject(MaintenanceStatusService);

  // URL prefixen wenn nicht bereits absolut (http/https). Statische Dateien aus `/assets/`
  // sind keine API-Aufrufe und werden ausgenommen — sonst landete ein `assets/...`-Abruf
  // unter `/api/v1/assets/...` und schlüge fehl (aufgefallen 2026-08-30 an der Prüfanleitung,
  // die die App aus `assets/hilfe/` lädt).
  const istStatischeDatei = req.url.startsWith('/assets/') || req.url.startsWith('assets/');
  const url = req.url.startsWith('http') || istStatischeDatei ? req.url : `${environment.apiUrl}${req.url}`;

  // Auth-Routen verwenden JSON (Symfony json_login erwartet JSON, kein MessagePack).
  // Bootstrap-Route läuft vor Login → ebenfalls JSON (simple Response, kein Redirect bei 401 nötig).
  // Invitation-Accept-Routen sind öffentlich und JSON-basiert (Token-authentifiziert).
  const isAuthRoute = url.includes('/auth/');
  const isBootstrapRoute = url.includes('/bootstrap/');
  const isPublicInvitationRoute = url.includes('/invitations/accept/');
  // SB-T9: Externer Stundennachweis-Erfassungsflow (accountlos, Token+OTP).
  // JSON-Passthrough analog Bootstrap/Invitation — kein msgpack, kein arraybuffer,
  // kein X-Mandant-Id (der Service setzt stattdessen X-Stundennachweis-Session).
  const isExternStundennachweisRoute = url.includes('/extern/stundennachweis');

  // W7-T3: DMS-Admin-Routes liefern/erwarten JSON (Backend-Konvention #13 — DocumentController,
  // DocumentClassificationController, DocumentRelationTypeController, HashPrefetchController
  // + 7 Sub-Controller (ProjectDocumentController, etc.) nutzen JsonResponse + #[MapRequestPayload].
  // msgpack-Encode/Decode wäre Asymmetrie.
  const DMS_JSON_PATH_PREFIXES = [
    '/api/v1/documents',
    '/api/v1/document-classifications',
    '/api/v1/document-relation-types',
    '/api/v1/uploads/by-hash',
    '/api/v1/dms/relation-preselects',
    '/api/v1/system/dms/',
    '/api/v1/bestandsknoten',
    '/api/v1/flurstuecke',
    // Task 9/10: WissensdatenbankController spricht JSON (#[MapRequestPayload] + JsonResponse,
    // analog DocumentController) — kein msgpack.
    '/api/v1/wissensdatenbank',
  ];
  // 7 Per-Entity-Sub-Controller (W4): /api/v1/{entityType}/{id}/documents[/{linkId}]
  const DMS_SUBCONTROLLER_REGEX =
    /\/api\/v1\/(projects|vertraege|leistungsbilder|leistungsphasen|teilleistungen|organisationen|ansprechpartner)\/[^/]+\/documents(\/|$)/;

  // W11-T5: 6 Mandant-Admin-Routes (JSON-Codec statt msgpack)
  // Wartungsmodus 2026-05-28: maintenance-mode-Route ergänzt (Controller nutzt
  // #[MapRequestPayload] + JsonResponse — msgpack-Body brachte HTTP 415).
  // feat/identifier-first-login: sso-config-Routes ergänzt (SsoConfigController
  // nutzt JsonResponse; test-connectivity ist POST mit JSON-Body).
  const MANDANT_ADMIN_JSON_PATHS_REGEX = [
    /\/api\/v1\/mandanten\/[0-9a-fA-F-]{36}\/storage-status$/,
    /\/api\/v1\/mandanten\/[0-9a-fA-F-]{36}\/storage-test$/,
    /\/api\/v1\/mandanten\/[0-9a-fA-F-]{36}\/storage-quota-request$/,
    /\/api\/v1\/mandanten\/[0-9a-fA-F-]{36}\/encryption-status$/,
    /\/api\/v1\/mandanten\/[0-9a-fA-F-]{36}\/tsa-config$/,
    /\/api\/v1\/mandanten\/[0-9a-fA-F-]{36}\/tsa-test$/,
    /\/api\/v1\/mandanten\/[0-9a-fA-F-]{36}\/maintenance-mode$/,
    /\/api\/v1\/mandanten\/[0-9a-fA-F-]{36}\/sso-config(\/test-connectivity)?$/,
    // Task 9 (PDF-Plattform Welle 2): MandantDokumentBrandingController nutzt
    // #[MapRequestPayload] + JsonResponse (kein msgpack) fuer alle acht Routen.
    // /dokument-branding/vorschau UND /dokument-logo liefern zusaetzlich Blob-
    // Bodies (PDF bzw. PNG/JPEG) — der msgpack-Pfad wuerde `responseType` hart
    // mit 'arraybuffer' ueberschreiben und den Bildinhalt wegdekodieren, ein
    // `FormData`-Logo-Upload liefe durch den msgpack-Encoder. `(\?.*)?$` statt
    // nacktem `$`, damit ein spaeterer Query-Parameter (z.B. Cache-Buster) den
    // Match nicht bricht.
    /\/api\/v1\/mandanten\/[0-9a-fA-F-]{36}\/dokument-branding(\?.*)?$/,
    /\/api\/v1\/mandanten\/[0-9a-fA-F-]{36}\/dokument-branding\/textbausteine(\?.*)?$/,
    /\/api\/v1\/mandanten\/[0-9a-fA-F-]{36}\/dokument-branding\/vorschau(\?.*)?$/,
    /\/api\/v1\/mandanten\/[0-9a-fA-F-]{36}\/dokument-logo(\?.*)?$/,
  ];

  // W12-T5: 6 System-Admin-Routes (JSON-Codec statt msgpack)
  // W12-T7: Storage-Pool CRUD-Endpoints ergänzt
  // Hotfix 2026-05-26: mandanten-storage-Liste + assign-pool/reprovision/migrate-pool ergänzt
  const SYSTEM_ADMIN_JSON_PATHS_REGEX = [
    /^\/api\/v1\/system\/mandanten-storage$/,
    /^\/api\/v1\/system\/mandanten\/[^/]+\/quota$/,
    /^\/api\/v1\/system\/mandanten\/[^/]+\/storage\/(?:assign-pool|reprovision|migrate-pool)$/,
    /^\/api\/v1\/system\/storage-migrations$/,
    /^\/api\/v1\/system\/storage-migrations\/[^/]+(?:\/(?:pause|resume|cancel|failures))?$/,
    /^\/api\/v1\/system\/audit\/(?:auth|documents)$/,
    /^\/api\/v1\/system\/master-key\/status$/,
    /^\/api\/v1\/system\/maintenance-mode$/,
    /^\/api\/v1\/system\/storage-pools$/,
    /^\/api\/v1\/system\/storage-pools\/[^/]+(?:\/(?:deletion-preview|test-connection|set-default|mandanten))?$/,
    // Urteilsanbindung (System-Admin, JSON-Codec)
    /^\/api\/v1\/system\/urteil-sync-profile$/,
    /^\/api\/v1\/system\/urteil-sync-profile\/sync-all$/,
    /^\/api\/v1\/system\/urteil-sync-profile\/[^/]+\/laeufe$/,
    // Deckt auch die statische Route POST /urteil-sync-profile/test ab
    // ("Profil testen", Ein-Segment-Suffix matcht [^/]+) — dokumentiert-abgesichert.
    /^\/api\/v1\/system\/urteil-sync-profile\/[^/]+(?:\/sync)?$/,
    /^\/api\/v1\/system\/urteile$/,
    /^\/api\/v1\/system\/urteile\/[^/]+$/,
    /^\/api\/v1\/system\/urteile\/[^/]+\/pruefkriterien-ableiten$/,
    /^\/api\/v1\/system\/urteile\/pruefkriterien-ableiten-sammel$/,
    // KI-Vorqualifizierung (Task 12): /{id}/vorqualifizieren, /{id}/ki-einstufung,
    // /{id}/pruefkriterien sind 2-Segment-Sub-Routen und von den obigen Regexes
    // NICHT abgedeckt; /vorqualifizieren-sammel matcht zwar zufällig auf
    // `urteile\/[^/]+$`, wird hier aber explizit dokumentiert-abgesichert.
    /^\/api\/v1\/system\/urteile\/vorqualifizieren-sammel$/,
    /^\/api\/v1\/system\/urteile\/[^/]+\/(?:vorqualifizieren|ki-einstufung|pruefkriterien)$/,
    // Urteil-Kategorien-Stammdaten (geführtes Tagging, JSON-Codec —
    // UrteilKategorieController nutzt #[MapRequestPayload] + JsonResponse)
    /^\/api\/v1\/system\/urteil-kategorien$/,
    /^\/api\/v1\/system\/urteil-kategorien\/[^/]+(?:\/merge)?$/,
    // Urteil-Lookups (Dropdown-Optionen)
    /^\/api\/v1\/system\/urteil-lookups\/(?:entscheidungstypen|gerichtsbarkeiten|bundeslaender|gerichte|refresh)/,
    // KI-Infrastruktur (System-Admin, JSON-Codec)
    /^\/api\/v1\/system\/ki\/verbindungen(?:\/[^/]+(?:\/(?:health-check|discovery|deletion-preview))?)?$/,
    /^\/api\/v1\/system\/ki\/avatare(?:\/[^/]+(?:\/(?:versionen|test-aufruf))?)?$/,
    /^\/api\/v1\/system\/ki\/modelle$/,
    /^\/api\/v1\/system\/ki\/tarife(?:\/refresh)?$/,
    /^\/api\/v1\/system\/ki\/kosten$/,
    /^\/api\/v1\/system\/ki\/pipelines(?:\/[^/]+)?$/,
    /^\/api\/v1\/system\/ki\/discovery-einstellung$/,
    // KI-Persona-Schicht (System-Admin, JSON-Codec — KiAbteilungController +
    // KiPersonaController nutzen #[MapRequestPayload] + JsonResponse)
    /^\/api\/v1\/system\/ki\/abteilungen(?:\/[^/]+)?$/,
    // Globaler Teamfoto-Trigger (POST, JSON-Codec)
    /^\/api\/v1\/system\/ki\/teambild$/,
    /^\/api\/v1\/system\/ki\/avatare\/[^/]+\/(?:casting|persona(?:\/neu-stylen)?)$/,
    /^\/api\/v1\/system\/ki\/casting-auftraege\/[^/]+$/,
    // Stimmen-Probe (Task 17) — Blob-Endpoint (audio/wav): MUSS im JSON-
    // Passthrough bleiben, weil der msgpack-Pfad responseType hart mit
    // 'arraybuffer' überschreibt und den WAV-Body wegdekodieren würde
    // (gleiche Begründung wie /ki-team/teambild).
    /^\/api\/v1\/system\/ki-vertonung\/stimmen\/[^/]+\/probe$/,
    // L2 Prüfkriterien-Katalog + Kuratierung (System-Admin/Kurator, JSON-Codec)
    /^\/api\/v1\/system\/pruefkriterien(?:\/[^/]+(?:\/(?:qualifizieren|freigeben|ablehnen|zurueckweisen|quell-urteil|ki-einschaetzung))?)?$/,
    // L2 Ableitungsaufträge (Liste/Detail) + Katalog-Einstellung (System-Admin, JSON-Codec)
    /^\/api\/v1\/system\/pruefkriterien\/auftraege(?:\/[^/]+)?$/,
    /^\/api\/v1\/system\/pruefkriterien\/einstellung$/,
    // Systemdiagnose (Task A3) — passive GET + /run POST
    /^\/api\/v1\/system\/environment-check(\/run)?$/,
    // Systemdiagnose „Worker & Queues" — read-only Queue-Status (JSON, JsonResponse)
    /^\/api\/v1\/system\/queue-status$/,
    // System-Logs (Task A4 — vorab eingetragen)
    /^\/api\/v1\/system\/logs(\/.*)?$/,
    // Scheduler (Task 7 + Politur) — SystemSchedulerController nutzt JsonResponse +
    // #[MapRequestPayload]; deckt /status, /jobs, /jobs/{id}, /jobs/{id}/run,
    // /jobs/{id}/runs und /jobs/{id}/runs/{runId} ab.
    /^\/api\/v1\/system\/scheduler\/(?:status|jobs(?:\/[^/]+(?:\/(?:run|runs(?:\/[^/]+)?))?)?)$/,
    // OCR-Diagnose-Export (Task 22, Spec 2026-08-10 §4.3 C9) — OcrDiagnoseController nutzt
    // #[MapRequestPayload] + JsonResponse für alle sechs Routen (Docblock der Klasse verlangt
    // diesen Eintrag explizit für Welle 4). /exporte/{id}/download liefert eine StreamedResponse
    // (application/zip) — MUSS ebenfalls im JSON-Passthrough bleiben, sonst überschreibt der
    // msgpack-Pfad `responseType` hart mit 'arraybuffer' und der ZIP-Body würde beim Decode-
    // Versuch zerstört (gleiche Begründung wie /dokument-branding/vorschau).
    //
    // **Dieser Eintrag ist load-bearing, nicht vorsorglich.** Die Diagnose-Seite lädt über
    // `OcrDiagnoseService.download()` mit `responseType: 'blob'` durch den HttpClient — also
    // genau hier durch. Fällt der Eintrag weg, zerstört der msgpack-Pfad nicht nur den ZIP-Body:
    // `error.error` ist dann auch kein `Blob` mehr, womit die Seite 410 `export_blob_unerreichbar`
    // nicht mehr von 503 `diagnose_ablage_nicht_aufloesbar` unterscheiden kann (zwei völlig
    // verschiedene Handlungsanweisungen an denselben Menschen).
    // **Neue OCR-Diagnose-Route? Hier nachtragen** — sonst läuft sie still über msgpack.
    /^\/api\/v1\/system\/ocr-diagnose\/kandidaten(\?.*)?$/,
    /^\/api\/v1\/system\/ocr-diagnose\/exporte(?:\/[^/]+(?:\/download)?)?(\?.*)?$/,
  ];

  // KI-Team-Seite (ALLE authentifizierten Nutzer — bewusst NICHT im
  // SYSTEM_ADMIN-Array, dessen Name den Auth-Scope beschreibt; technisch
  // bedeuten alle Arrays nur "JSON-Passthrough statt msgpack").
  // KiTeamController nutzt JsonResponse. Der Bild-Endpoint MUSS hier ebenfalls
  // matchen: der msgpack-Pfad überschreibt responseType hart mit 'arraybuffer'
  // (s.u.), ein `responseType: 'blob'` im Service würde sonst zu einem
  // wegdekodierten Body führen (gleicher Grund, warum /api/v1/documents im
  // DMS-Prefix-Array steht).
  const KI_TEAM_JSON_PATHS_REGEX = [
    /^\/api\/v1\/ki-team$/,
    /^\/api\/v1\/ki-team\/bilder\/[^/]+\/[^/]+$/,
    // Globales Gruppen-Teamfoto (Blob-Endpoint, gleiche responseType-Begründung wie /bilder/).
    /^\/api\/v1\/ki-team\/teambild$/,
    // Öffentlicher Team-Steckbrief (Spec §11a Punkt 4) — UUID-Pattern spiegelt
    // das Routen-Requirement im KiTeamController (keine Kollision mit
    // /teambild oder /bilder/...).
    /^\/api\/v1\/ki-team\/[0-9a-f-]{36}$/,
  ];

  // PDF-Plattform Welle 3, Aufgabe 13: interne Pruefbericht-Route liefert
  // application/pdf statt msgpack. Der msgpack-Pfad wuerde responseType hart
  // mit 'arraybuffer' ueberschreiben und den PDF-Body wegdekodieren (Bestands-
  // kommentar dokument-branding, gleiche Begruendung). Die externe Route
  // (/api/v1/extern/stundennachweis/pruefbericht) ist bereits ueber
  // isExternStundennachweisRoute Passthrough — bewusst NICHT in diesem Regex,
  // der matcht nur die interne, UUID-adressierte Route.
  const STUNDENNACHWEIS_BLOB_PATHS_REGEX = [/\/api\/v1\/stundennachweise\/[0-9a-fA-F-]{36}\/pruefbericht(\?.*)?$/];

  const isDmsJsonRoute =
    DMS_JSON_PATH_PREFIXES.some((p) => url.includes(p)) ||
    DMS_SUBCONTROLLER_REGEX.test(url) ||
    MANDANT_ADMIN_JSON_PATHS_REGEX.some((rx) => rx.test(url)) ||
    SYSTEM_ADMIN_JSON_PATHS_REGEX.some((rx) => rx.test(url)) ||
    KI_TEAM_JSON_PATHS_REGEX.some((rx) => rx.test(url)) ||
    STUNDENNACHWEIS_BLOB_PATHS_REGEX.some((rx) => rx.test(url));

  // X-Mandant-Id-Header setzen wenn ein aktiver Mandant gewählt wurde.
  // Gilt für alle authentifizierten Requests — auch /auth/me profitiert, damit
  // der Backend-TenantContext die mandantspezifischen Rollen korrekt auflöst.
  const mandantId = authService.activeMandantId();
  let headersWithMandant = req.headers;
  if (mandantId && !isBootstrapRoute && !isPublicInvitationRoute && !isExternStundennachweisRoute) {
    headersWithMandant = headersWithMandant.set('X-Mandant-Id', mandantId);
  }

  /**
   * `feld: Meldung` für eine einzelne Validierungsverletzung. Der Feldname macht
   * generische Backend-Meldungen überhaupt zuordenbar — ein nacktes „This value
   * should not be blank." bei ausgefülltem Formular ist nicht diagnostizierbar
   * (Anlass: leeres `storageObjectId` im Upload-Resume-Pfad, 2026-07-27).
   * Klassen-Level-Constraints kommen ohne `path` → nur die Meldung.
   */
  function formatiereViolation(violation: { path?: string; message: string }): string {
    const feld = violation.path?.trim();

    return feld ? `${feld}: ${violation.message}` : violation.message;
  }

  /**
   * Prüft ob eine Fehlerantwort dem RFC-7807-Format (application/problem+json) entspricht
   * und handelt entsprechend dem `code`-Feld:
   * - `maintenance_mode` → Toast (warning) + Banner-Refresh, kein Logout/Router.navigate
   * - `validation_failed` mit errors[] → Toast (error) mit zusammengefassten Fehlermeldungen
   * - Sonstige Codes → KEIN Interceptor-Toast; `false` (die Komponente ist die
   *   alleinige Quelle für die fachliche Fehlermeldung → verhindert Doppel-Toasts).
   * Gibt `true` zurück wenn der Fehler behandelt wurde.
   * Gibt `false` zurück wenn Content-Type kein problem+json enthält, Parsing scheitert
   *   oder der Code nicht interceptor-seitig behandelt wird (Komponente toastet selbst).
   * Seit Phase 3 sprechen alle Backend-Endpoints RFC 7807 — kein Legacy-503-Fallback nötig.
   */
  function handleProblemDetails(error: HttpErrorResponse): boolean {
    const contentType = error.headers?.get('content-type') ?? '';
    if (!contentType.includes('problem+json')) {
      return false;
    }
    const problem = parseProblemDetails(error);
    if (!problem) {
      return false;
    }
    if (problem.code === 'maintenance_mode') {
      toastService.warning(problem.detail ?? problem.title);
      maintenanceStatusService.refresh().subscribe({
        error: () => {
          /* ignore refresh errors */
        },
      });
      return true;
    }
    if (problem.code === 'validation_failed' && problem.errors?.length) {
      toastService.error(problem.errors.map(formatiereViolation).join('\n'));
      return true;
    }
    // Alle übrigen Codes (404/403/409/500/…) werden bewusst NICHT hier getoastet.
    // Die jeweilige Komponente besitzt einen eigenen `error:`-Handler mit
    // fachspezifischer Meldung (z.B. migrate-pool: "Für diesen Mandanten läuft
    // bereits eine Migration."). Ein generischer Interceptor-Toast würde diese
    // verdoppeln. `false` → catchError fällt auf den Decode-Re-Throw-Pfad durch,
    // sodass die Komponente die dekodierten ProblemDetails erhält.
    // (Entspricht dem Pre-Refactor-Verhalten von handle503Maintenance.)
    return false;
  }

  // SSE-Stream-Endpunkte: niemals msgpack — unverändert durchreichen.
  // (Die Streams laufen normalerweise über fetch am Interceptor vorbei; dies ist Absicherung,
  //  falls ein HttpClient-Pfad einen /stream-Endpunkt berührt.)
  // Statische Dateien gehen unveraendert durch: kein Mandant-Header, kein msgpack, kein
  // `responseType: 'arraybuffer'` — der msgpack-Pfad ueberschreibt ihn hart und machte aus der
  // als Text angeforderten Prüfanleitung einen ArrayBuffer (aufgefallen 2026-08-30).
  if (istStatischeDatei) {
    return next(req.clone({ url }));
  }

  const isSseStreamRoute = url.includes('/ki-chats/') && url.includes('/stream');
  if (isSseStreamRoute) {
    const streamReq = req.clone({ url, headers: headersWithMandant, withCredentials: true });
    return next(streamReq);
  }

  if (isAuthRoute || isBootstrapRoute || isPublicInvitationRoute || isDmsJsonRoute || isExternStundennachweisRoute) {
    const passthroughReq = req.clone({ url, headers: headersWithMandant, withCredentials: true });
    return next(passthroughReq).pipe(
      catchError((error: HttpErrorResponse) => {
        handleProblemDetails(error);
        return throwError(() => error);
      }),
    );
  }

  const needsBody = BODY_METHODS.includes(req.method);
  // FormData-Uploads (Multipart) NICHT msgpack-codieren — der Browser setzt
  // Content-Type: multipart/form-data + boundary automatisch. Response kommt
  // trotzdem msgpack zurück (Accept-Header bleibt gesetzt).
  const isMultipart = req.body instanceof FormData;

  // Request-Body mit MessagePack serialisieren
  // Vorher alle `undefined`-Felder rekursiv entfernen, damit sie nicht als msgpack-nil
  // auf dem Backend ankommen (Symfony würde sonst versuchen, required-Args mit null
  // zu befüllen — führt zu MissingConstructorArgumentsException).
  let body: ArrayBuffer | null = null;
  if (needsBody && !isMultipart && req.body !== null && req.body !== undefined) {
    const sanitized = stripUndefined(req.body);
    body = encode(sanitized).buffer as ArrayBuffer;
  }

  // Headers aufbauen — startend mit headersWithMandant (enthält ggf. X-Mandant-Id)
  let headers = headersWithMandant.set('Accept', 'application/x-msgpack');

  if (needsBody && !isMultipart) {
    headers = headers.set('Content-Type', 'application/x-msgpack');
  }

  const cloned = req.clone({
    url,
    headers,
    withCredentials: true,
    body: needsBody ? (isMultipart ? req.body : (body ?? req.body)) : req.body,
    responseType: 'arraybuffer',
  });

  return next(cloned).pipe(
    map((event) => {
      if (event instanceof HttpResponse && event.body instanceof ArrayBuffer && event.body.byteLength > 0) {
        // Nutze decodeBodyBuffer statt decode() direkt, damit JSON-Antworten (z.B.
        // LeistungsbildAenderungsHistorieController, der $this->json() verwendet)
        // korrekt deserialisiert werden — nicht alle GET-Endpoints liefern msgpack.
        const ct = event.headers.get('Content-Type');
        const decoded = decodeBodyBuffer(event.body, ct);
        return event.clone({ body: decoded });
      }
      return event;
    }),
    catchError((error: HttpErrorResponse) => {
      // Non-2xx → in Debug-Log (localStorage-Ringbuffer + console.error) schreiben,
      // damit der User / Claude den Verlauf nachvollziehen kann (`__hmDebugLog()` in der Console).
      // 401 ausgenommen, weil das der normale Session-Ablauf ist und der Router gleich weiterleitet.
      if (error.status !== 401) {
        const detail = parseProblemDetails(error)?.detail ?? (error.message || null);
        debugLog.logHttpError(req.method, safePath(error.url), error.status || 0, detail);
      }
      // application/problem+json → RFC-7807-Handler (alle Backend-Endpoints seit Phase 3).
      if (handleProblemDetails(error)) {
        return throwError(() => error);
      }
      if (error.status === 401) {
        router.navigate(['/login']);
      }
      // Error-Body kann je nach Quelle msgpack ODER JSON sein:
      //  - Erfolgsantworten der Controller: application/x-msgpack
      //  - Error-Antworten via GlobalExceptionHandler: application/json
      //  (siehe backend/src/Inthepro/ErrorHandler/GlobalExceptionHandler.php)
      // Da unsere Requests `responseType: 'arraybuffer'` setzen, kommt der
      // Body in beiden Fällen als ArrayBuffer durch und muss vor dem Re-Throw
      // dekodiert werden — sonst sehen Komponenten nur einen rohen Buffer
      // unter `err.error` und greifen auf die generische Fallback-Toast-
      // Meldung zurück statt auf die eigentliche Backend-Begründung
      // (z.B. "Diese E-Mail-Adresse ist bereits Mitglied dieses Mandanten."
      // bei 409 ConflictHttpException).
      if (error.error instanceof ArrayBuffer && error.error.byteLength > 0) {
        const decoded = decodeBodyBuffer(error.error, error.headers?.get('Content-Type') ?? null);
        if (decoded !== null) {
          const decodedError = new HttpErrorResponse({
            error: decoded,
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url ?? undefined,
          });
          return throwError(() => decodedError);
        }
        // Decode in beiden Encodings fehlgeschlagen → Original-Error durchreichen.
      }
      return throwError(() => error);
    }),
  );
};
